import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Layers, AlertTriangle, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import AbbrTip from '@/components/AbbrTip';
import { usePortfolioScopeParams } from '@/lib/portfolioScopeParams';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// P95 — Single Multi-Share Sell widget (Jun 2026).
//
// Sell N shares across MULTIPLE tax lots in one click using a selectable
// cost-basis method. Rendered as the top row of the lot list on BOTH
// surfaces: the Portfolio page row expansion AND the Stock Modal's
// "My Portfolio" tab. Live preview shows exactly which lots get
// consumed (with per-lot ST/LT gain/loss) BEFORE the user commits.
//
// FIFO is pre-checked — IRS legal default + Robinhood's only behavior.
// Checkboxes are mutually exclusive (radio behavior per spec). Every
// method label is an <AbbrTip> popover ending in a Glossary link.
const METHODS = [
  { id: 'fifo', label: 'FIFO', term: 'FIFO' },
  { id: 'lifo', label: 'LIFO', term: 'LIFO' },
  { id: 'hifo', label: 'HIFO', term: 'HIFO' },
  { id: 'lofo', label: 'Low Cost', term: 'LOFO' },
  { id: 'optimizer', label: 'Tax Lot Optimizer', term: 'TAX_LOT_OPTIMIZER' },
];

const fmtMoney = (n) => {
  const v = Number(n) || 0;
  const abs = Math.abs(v).toFixed(2);
  return v < 0 ? `-$${abs}` : `$${abs}`;
};

const fmtShares = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? String(v) : v.toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
};

function GainLoss({ value }) {
  const v = Number(value) || 0;
  return (
    <span className={`font-mono font-semibold tabular-nums ${v < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
      {v >= 0 ? '+' : ''}{fmtMoney(v)}
    </span>
  );
}

function TermBadge({ term }) {
  return (
    <span
      className={`inline-flex px-1 py-0.5 rounded text-[9px] font-bold leading-none cursor-help ${
        term === 'LT'
          ? 'bg-indigo-100 dark:bg-indigo-950 text-indigo-700 dark:text-indigo-300'
          : 'bg-amber-100 dark:bg-amber-950 text-amber-700 dark:text-amber-300'
      }`}
      title={TERM_TIPS[term]}
    >
      {term}
    </span>
  );
}

// Hover explainers for the ST/LT pills + totals split.
const TERM_TIPS = {
  ST: 'ST = Short-Term: this lot was held 1 year or less. Gains are taxed at your ordinary income rate (up to 37% federal).',
  LT: 'LT = Long-Term: this lot was held more than 1 year. Gains get the preferential 0/15/20% capital-gains rate.',
};

// Today in LOCAL time as YYYY-MM-DD (toISOString would shift to UTC and
// show yesterday for US evenings).
const todayLocalISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export default function SingleMultiShareSell({ ticker, totalShares, currentPrice, onSold }) {
  const [open, setOpen] = useState(false);
  const [shares, setShares] = useState('');
  const [price, setPrice] = useState('');
  const [saleDate, setSaleDate] = useState('');
  const [method, setMethod] = useState('fifo');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState('');
  const scopeParams = usePortfolioScopeParams();
  const pfId = scopeParams.portfolio_id || null;

  // Debounced live preview — fires whenever shares + sale terms are valid.
  useEffect(() => {
    if (!open) return undefined;
    const n = parseFloat(shares);
    const p = parseFloat(price);
    if (!n || n <= 0 || !p || p <= 0 || !saleDate) {
      setPreview(null);
      setError('');
      return undefined;
    }
    setPreviewLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await axios.post(
          `${API}/portfolio/positions/${ticker}/multi-sell/preview`,
          { shares_to_sell: n, method, portfolio_id: pfId, sale_price: p, sale_date: saleDate },
          { withCredentials: true }
        );
        setPreview(data);
        setError('');
      } catch (e) {
        setPreview(null);
        setError(e?.response?.data?.detail || 'Could not build a preview');
      } finally {
        setPreviewLoading(false);
      }
    }, 450);
    return () => clearTimeout(t);
  }, [open, shares, price, saleDate, method, ticker, pfId]);

  const execute = async () => {
    if (!preview?.confirm_token || executing) return;
    setExecuting(true);
    try {
      const { data } = await axios.post(
        `${API}/portfolio/positions/${ticker}/multi-sell/execute`,
        { shares_to_sell: parseFloat(shares), method, portfolio_id: pfId, sale_price: parseFloat(price), sale_date: saleDate, confirm_token: preview.confirm_token },
        { withCredentials: true }
      );
      const lotCount = data?.sold_lots?.length || 0;
      toast.success(
        `Sold ${fmtShares(data?.totals?.total_shares)} ${ticker} across ${lotCount} lot${lotCount === 1 ? '' : 's'} — ${fmtMoney(data?.proceeds)} added to cash`
      );
      setOpen(false);
      setShares('');
      setMethod('fifo');
      setPreview(null);
      window.dispatchEvent(new Event('cash-changed'));
      onSold?.(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Sell failed — try refreshing the preview');
    } finally {
      setExecuting(false);
    }
  };

  const hasWashWarning = (preview?.warnings || []).some((w) => w.startsWith('wash_sale'));
  const hasSameDay = (preview?.warnings || []).includes('same_day_buy');

  return (
    <div className="border-b border-border/50 bg-muted/20" data-testid={`multi-sell-widget-${ticker}`}>
      <button
        type="button"
        onClick={() => {
          setOpen((v) => {
            const next = !v;
            // Pre-fill sale terms on open — current price + today, both
            // fully editable (the user owns the final numbers).
            if (next) {
              if (!price && Number(currentPrice) > 0) setPrice(Number(currentPrice).toFixed(2));
              if (!saleDate) setSaleDate(todayLocalISO());
            }
            return next;
          });
          setError('');
        }}
        className="w-full flex items-center justify-between gap-2 px-4 py-2 text-left hover:bg-muted/40 transition-colors"
        data-testid={`multi-sell-toggle-${ticker}`}
        title="Sell N shares across multiple tax lots in one go — FIFO, LIFO, HIFO, Low Cost, or the Tax Lot Optimizer"
      >
        <span className="flex items-center gap-2 text-xs font-semibold text-foreground">
          <Layers className="w-3.5 h-3.5 text-primary" />
          Sell Multiple Shares
          <span className="font-normal text-muted-foreground hidden sm:inline">
            — one sell across several lots, 5 cost-basis methods
          </span>
        </span>
        {open ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
      </button>

      {open && (
        <div className="px-4 pb-3 space-y-2.5" data-testid={`multi-sell-panel-${ticker}`}>
          {/* Shares input */}
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[11px] font-semibold text-muted-foreground" htmlFor={`multi-sell-shares-${ticker}`}>
              Shares to sell:
            </label>
            <input
              id={`multi-sell-shares-${ticker}`}
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              value={shares}
              onChange={(e) => setShares(e.target.value)}
              placeholder="0.0000"
              className="w-28 px-2 py-1 text-xs font-mono rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid={`multi-sell-shares-input-${ticker}`}
            />
            <span className="text-[11px] text-muted-foreground">
              of <span className="font-mono font-semibold text-foreground">{fmtShares(totalShares)}</span> owned
            </span>
          </div>

          {/* Sale terms — $/share + date sold, proceeds derived live */}
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[11px] font-semibold text-muted-foreground" htmlFor={`multi-sell-price-${ticker}`}>
              $ / share:
            </label>
            <input
              id={`multi-sell-price-${ticker}`}
              type="number"
              inputMode="decimal"
              min="0"
              step="any"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              placeholder="0.00"
              className="w-24 px-2 py-1 text-xs font-mono rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid={`multi-sell-price-input-${ticker}`}
              title="The price per share you actually sold at — pre-filled with the current price, edit to match your broker's fill"
            />
            <label className="text-[11px] font-semibold text-muted-foreground" htmlFor={`multi-sell-date-${ticker}`}>
              Date sold:
            </label>
            <input
              id={`multi-sell-date-${ticker}`}
              type="date"
              value={saleDate}
              onChange={(e) => setSaleDate(e.target.value)}
              className="px-2 py-1 text-xs font-mono rounded-md border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary"
              data-testid={`multi-sell-date-input-${ticker}`}
              title="The date the sale happened — drives the short-term vs long-term split and the wash-sale window"
            />
            {parseFloat(shares) > 0 && parseFloat(price) > 0 && (
              <span className="text-[11px] text-muted-foreground" data-testid={`multi-sell-proceeds-${ticker}`}>
                Proceeds: <span className="font-mono font-bold text-amber-600 dark:text-amber-400">{fmtMoney(parseFloat(shares) * parseFloat(price))}</span>
              </span>
            )}
          </div>

          {/* Cost-basis method — checkboxes with radio behavior */}
          <div className="flex items-center gap-x-4 gap-y-1.5 flex-wrap">
            {METHODS.map((m) => (
              <span key={m.id} className="inline-flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={method === m.id}
                  onChange={() => setMethod(m.id)}
                  className="w-3.5 h-3.5 accent-primary cursor-pointer"
                  data-testid={`multi-sell-method-${m.id}-${ticker}`}
                  aria-label={`Use ${m.label} cost-basis method`}
                />
                <span className="text-[11px] font-semibold">
                  <AbbrTip term={m.term}>{m.label}</AbbrTip>
                </span>
              </span>
            ))}
          </div>

          {error && (
            <p className="text-[11px] text-rose-600 dark:text-rose-400 font-semibold" data-testid={`multi-sell-error-${ticker}`}>
              {error}
            </p>
          )}

          {previewLoading && !preview && (
            <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin" /> Building preview…
            </p>
          )}

          {/* Live preview — which lots get consumed */}
          {preview && (
            <div className="rounded-md border border-border/70 overflow-hidden" data-testid={`multi-sell-preview-${ticker}`}>
              <div className="px-2.5 py-1.5 bg-muted/40 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Preview — lots consumed ({preview.method?.toUpperCase()})
              </div>
              <div className="divide-y divide-border/50">
                {(preview.lots_consumed || []).map((row) => (
                  <div
                    key={row.position_id}
                    className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-[11px] flex-wrap"
                    data-testid={`multi-sell-preview-row-${row.position_id}`}
                  >
                    <span className="text-muted-foreground">
                      {row.purchase_date} <span className="font-mono">@ ${Number(row.cost_per_share).toFixed(2)}</span>
                      {row.is_drip ? <span className="ml-1 text-sky-500" title="DRIP lot">💧</span> : null}
                    </span>
                    <span className="font-mono font-semibold tabular-nums">→ {fmtShares(row.shares_pulled)} shs</span>
                    <span className="flex items-center gap-1.5">
                      <TermBadge term={row.term} />
                      <GainLoss value={row.gain_loss} />
                    </span>
                  </div>
                ))}
              </div>
              <div
                className="flex items-center justify-between gap-2 px-2.5 py-1.5 border-t border-border bg-muted/30 text-[11px] font-semibold flex-wrap"
                data-testid={`multi-sell-preview-total-${ticker}`}
              >
                <span>
                  Total: <span className="font-mono">{fmtShares(preview.totals?.total_shares)}</span> shs
                  · proceeds <span className="font-mono">{fmtMoney(preview.totals?.estimated_proceeds)}</span>
                </span>
                <span className="flex items-center gap-2">
                  <span className="text-muted-foreground font-normal">
                    <span className="cursor-help underline decoration-dotted" title={TERM_TIPS.ST}>ST</span> <GainLoss value={preview.totals?.st_gain_loss} /> · <span className="cursor-help underline decoration-dotted" title={TERM_TIPS.LT}>LT</span> <GainLoss value={preview.totals?.lt_gain_loss} />
                  </span>
                  <GainLoss value={preview.totals?.total_gain_loss} />
                </span>
              </div>
            </div>
          )}

          {/* Wash-sale awareness — display only, never blocks */}
          {preview && hasWashWarning && (
            <div
              className="flex items-start gap-2 px-2.5 py-2 rounded-md bg-amber-50 dark:bg-amber-950/40 ring-1 ring-amber-400/40 text-[11px] text-amber-800 dark:text-amber-300"
              data-testid={`multi-sell-wash-banner-${ticker}`}
            >
              <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <span>
                <strong>Wash sale risk:</strong> this sale realizes a loss. To preserve the tax
                deduction, don&apos;t repurchase {ticker} (and turn off DRIP) for 30 days from
                today — the safe re-buy day is Day 31.{' '}
                <a href="/learn/cost-basis-methods-fifo-lifo-hifo" className="underline font-semibold" target="_blank" rel="noreferrer">
                  Learn: wash sale rules →
                </a>
              </span>
            </div>
          )}
          {preview && hasSameDay && (
            <p className="text-[10px] text-muted-foreground italic">
              ⚠ One of these lots was bought today — same-day buy + sell can trigger day-trade rules at your broker.
            </p>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 pt-0.5">
            <button
              type="button"
              onClick={() => { setOpen(false); setShares(''); setPreview(null); setError(''); }}
              className="px-3 py-1.5 text-[11px] font-semibold rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-colors"
              data-testid={`multi-sell-cancel-${ticker}`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={execute}
              disabled={!preview?.confirm_token || executing || previewLoading}
              className="px-3 py-1.5 text-[11px] font-bold rounded-md bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid={`multi-sell-execute-${ticker}`}
            >
              {executing ? 'Selling…' : 'Execute sell'}
            </button>
          </div>
          <p className="text-[10px] text-muted-foreground">
            DiviDrip records the sale here only — place the matching trade with your real broker.
            Robinhood users: pick FIFO, that&apos;s what Robinhood will actually do.
          </p>
        </div>
      )}
    </div>
  );
}
